/**
 * OrbAppPage — Standalone fullscreen Voice Orb
 * ===============================================
 * Route: /orb-app
 * Features:
 * - Integrated login (no redirect needed, works in APK)
 * - Continuous listening after AI response
 * - Ping sounds on state transitions
 * - Push notifications on ride creation
 * - Pure black fullscreen, zero chrome
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Mic, Wifi, WifiOff, LogIn, LogOut } from 'lucide-react';
import axios from 'axios';
import { LanguageFlags } from '../components/LanguageFlags';
import VoiceOrb from '../components/VoiceOrb';
import { MatchCard } from '../components/MatchCard';
import { OrbDetourSlider } from '../components/OrbDetourSlider';
import useOrbSocket from '../hooks/useOrbSocket';
import useAudioRecorder from '../hooks/useAudioRecorder';
import { API } from '../utils/apiConfig';

// --- Ping Sound Generator (Web Audio API) ---
const AudioCtx = window.AudioContext || window.webkitAudioContext;
let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) _audioCtx = new AudioCtx();
  // Resume suspended context (required after user gesture on mobile/APK)
  if (_audioCtx.state === 'suspended') _audioCtx.resume();
  return _audioCtx;
}

function playPing(freq = 880, duration = 0.08, vol = 0.15) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch {}
}

const PING = {
  listening: () => playPing(660, 0.06, 0.12),   // low ping = mic on
  processing: () => playPing(880, 0.08, 0.1),   // mid ping = thinking
  speaking: () => playPing(1100, 0.1, 0.08),    // high ping = AI speaks
};

// --- Inline Login Component ---
function OrbLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        onLoginSuccess(data);
      } else {
        setError(data.error || 'Login fehlgeschlagen');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="orb-login-screen" style={{
      position: 'fixed', inset: 0, background: '#000',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        background: 'rgba(255,255,255,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24,
      }}>
        <LogIn size={22} color="rgba(255,255,255,0.4)" />
      </div>

      <h2 style={{ color: '#e2e8f0', fontSize: 18, fontWeight: 600, margin: '0 0 6px' }}>
        VR Guardian
      </h2>
      <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, margin: '0 0 20px' }}>
        Melde dich an, um den Orb zu nutzen
      </p>

      <div style={{ marginBottom: 20, width: '100%', maxWidth: 300 }}>
        <LanguageFlags dark={true} />
      </div>

      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 300 }}>
        <input
          data-testid="orb-login-email"
          type="email"
          placeholder="E-Mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{
            width: '100%', padding: '12px 16px', marginBottom: 12,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10, color: '#e2e8f0', fontSize: 15,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
        <input
          data-testid="orb-login-password"
          type="password"
          placeholder="Passwort"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{
            width: '100%', padding: '12px 16px', marginBottom: 16,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10, color: '#e2e8f0', fontSize: 15,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
        {error && (
          <div data-testid="orb-login-error" style={{
            color: '#ef4444', fontSize: 13, marginBottom: 12, textAlign: 'center',
          }}>{error}</div>
        )}
        <button
          data-testid="orb-login-submit"
          type="submit"
          disabled={loading}
          style={{
            width: '100%', padding: '12px 0',
            background: loading ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 10, color: '#e2e8f0', fontSize: 15, fontWeight: 500,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? '...' : 'Anmelden'}
        </button>
      </form>

      <a
        data-testid="orb-login-register-link"
        href="/register"
        style={{
          marginTop: 20, fontSize: 12, color: 'rgba(255,255,255,0.25)',
          textDecoration: 'none',
        }}
      >
        Noch kein Konto? Registrieren
      </a>
    </div>
  );
}

// --- Main Orb Page ---
export default function OrbAppPage() {
  const lang = (localStorage.getItem('i18nextLng') || 'de').startsWith('en') ? 'en' : 'de';
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));

  const {
    orbState, transcript, responseText, responseAudio,
    isConnected, error, sendAudio, sendDone,
    matchResult, clearMatch,
  } = useOrbSocket(isLoggedIn);

  const {
    startRecording, stopRecording,
    audioLevel, stressLevel, isRecording,
  } = useAudioRecorder();

  const [statusText, setStatusText] = useState('');
  const [autoListen, setAutoListen] = useState(true);
  const audioPlayerRef = useRef(null);
  const autoListenTimerRef = useRef(null);
  const prevStateRef = useRef('idle');

  // ── Orb is voice-only — no DeepL translation here. ─────────────
  // Live translation is opt-in via the global Globe button (TranslationContext).

  // Logout handler
  const handleLogout = () => {
    window.speechSynthesis?.cancel();
    localStorage.removeItem('token');
    setIsLoggedIn(false);
  };

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = '';
    };
  }, []);

  // SSE listener for push notifications
  useEffect(() => {
    if (!isLoggedIn) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const es = new EventSource(`${API}/orchestrator/events/stream`);
    es.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data);
        if (event.event_type === 'push_notification') {
          const d = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(d.title || 'VoiceRide', {
              body: d.body || '', icon: '/favicon.ico',
              tag: `ride-${d.ride_id || 'unknown'}`,
            });
          }
        }
      } catch {}
    };
    return () => es.close();
  }, [isLoggedIn]);

  // Ping sounds on state transitions
  useEffect(() => {
    const current = isRecording ? 'listening' : orbState;
    if (current !== prevStateRef.current) {
      if (PING[current]) PING[current]();
      prevStateRef.current = current;
    }
  }, [orbState, isRecording]);

  // Status text — Orb shows the actual responseText (no translation here)
  useEffect(() => {
    if (error) {
      setStatusText(error);
    } else if (responseText && (orbState === 'speaking' || orbState === 'idle')) {
      setStatusText(responseText);
    } else if (transcript && orbState === 'processing') {
      setStatusText(`"${transcript}"`);
    } else if (isRecording) {
      setStatusText('');
    } else if (orbState === 'idle') {
      setStatusText(lang === 'de' ? 'Tippe zum Sprechen' : 'Tap to speak');
    }
  }, [orbState, transcript, responseText, error, lang, isRecording]);

  // Continuous listening: auto-restart mic after audio ends
  const restartListening = useCallback(async () => {
    if (!autoListen) return;
    autoListenTimerRef.current = setTimeout(async () => {
      if (audioPlayerRef.current) return;
      const result = await startRecording(10000);
      if (result) {
        sendAudio(result.base64, result.mimeType);
      }
    }, 600);
  }, [autoListen, startRecording, sendAudio]);

  // Audio playback via Blob
  useEffect(() => {
    if (!responseAudio?.b64) return;
    let objectUrl = null;
    try {
      const bytes = Uint8Array.from(atob(responseAudio.b64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: responseAudio.mimeType || 'audio/mpeg' });
      objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audioPlayerRef.current = audio;
      const cleanup = () => {
        audioPlayerRef.current = null;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        sendDone();
        restartListening();
      };
      audio.onended = cleanup;
      audio.onerror = () => { cleanup(); };
      audio.play().catch(() => { cleanup(); });
    } catch {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      sendDone();
      restartListening();
    }
  }, [responseAudio, sendDone, restartListening]);

  // Text-only response → use Web Speech API as client-side TTS fallback (Orb language only)
  useEffect(() => {
    if (responseText && !responseAudio?.b64 && orbState === 'speaking') {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(responseText);
        utter.lang = lang === 'de' ? 'de-DE' : (lang === 'en' ? 'en-US' : lang);
        utter.rate = 1.0;
        utter.onend = () => { sendDone(); restartListening(); };
        utter.onerror = () => { sendDone(); restartListening(); };
        window.speechSynthesis.speak(utter);
      } else {
        autoListenTimerRef.current = setTimeout(() => {
          sendDone(); restartListening();
        }, 2000);
      }
    }
    return () => {
      if (autoListenTimerRef.current) clearTimeout(autoListenTimerRef.current);
    };
  }, [responseText, responseAudio, orbState, restartListening, sendDone, lang]);

  // Orb tap — also initializes AudioContext on first gesture (mobile requirement)
  const handleOrbTap = useCallback(async () => {
    // Initialize audio context on first user gesture (mobile/APK requirement)
    try { getAudioCtx(); } catch {}

    if (orbState === 'processing') return;
    // Allow tap during speaking to interrupt
    if (orbState === 'speaking' && audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
      sendDone();
    }
    if (autoListenTimerRef.current) {
      clearTimeout(autoListenTimerRef.current);
      autoListenTimerRef.current = null;
    }
    if (isRecording) {
      stopRecording();
    } else {
      const result = await startRecording(10000);
      if (result) sendAudio(result.base64, result.mimeType);
    }
  }, [orbState, isRecording, startRecording, stopRecording, sendAudio, sendDone, autoListen]);

  // Login check
  if (!isLoggedIn) {
    return <OrbLogin onLoginSuccess={() => setIsLoggedIn(true)} />;
  }

  const visualState = isRecording ? 'listening' : orbState;

  return (
    <div
      data-testid="orb-app-page"
      style={{
        position: 'fixed', inset: 0, background: '#000',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', zIndex: 99999,
        touchAction: 'none', userSelect: 'none',
      }}
    >
      {/* Connection indicator (label + dot) */}
      <div data-testid="orb-app-connection" style={{
        position: 'absolute', top: 16, right: 16,
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, color: isConnected ? '#22c55e' : '#ef4444',
        background: isConnected ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
        border: `1px solid ${isConnected ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
        borderRadius: 999, padding: '4px 10px',
        fontWeight: 600,
      }}>
        {isConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
        <span data-testid="orb-app-connection-label">
          {isConnected ? 'Verbunden' : 'Nicht verbunden'}
        </span>
      </div>

      {/* Stress indicator */}
      {isRecording && (
        <div data-testid="orb-app-stress" style={{
          position: 'absolute', top: 18, left: 16,
          width: 8, height: 8, borderRadius: '50%',
          background: { calm: '#22c55e', normal: '#3b82f6', stress: '#f59e0b', danger: '#ef4444' }[stressLevel],
          transition: 'background 0.3s',
        }} />
      )}

      {/* Logout button */}
      <button
        data-testid="orb-logout-btn"
        onClick={handleLogout}
        style={{
          position: 'absolute', top: 14, left: isRecording ? 32 : 14,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, padding: '6px 10px',
          color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 5,
          fontSize: 11, transition: 'all 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#ef4444'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
      >
        <LogOut size={13} /> Abmelden
      </button>

      {/* The Orb */}
      <div data-testid="orb-app-orb-container" style={{ position: 'relative', cursor: 'pointer' }}>
        <VoiceOrb state={visualState} audioLevel={audioLevel} size={300} onClick={handleOrbTap} />
        {/* Auto-listen pulse ring — fires when mic auto-arms after a greeting/response */}
        {isRecording && (
          <div
            data-testid="orb-listen-pulse-ring"
            aria-hidden="true"
            style={{
              position: 'absolute', inset: -10,
              borderRadius: '50%',
              border: '2px solid rgba(67,233,123,0.55)',
              pointerEvents: 'none',
              animation: 'orbListenPulse 1.4s ease-out infinite',
            }}
          />
        )}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          opacity: orbState === 'idle' && !isRecording ? 0.2 : 0,
          transition: 'opacity 0.4s ease',
        }}>
          <Mic size={32} color="#fff" />
        </div>
      </div>

      {/* Auto-listen "Sprich jetzt"-Hint */}
      {isRecording && (
        <div
          data-testid="orb-listen-hint"
          style={{
            marginTop: 14,
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: 13, fontWeight: 700, letterSpacing: 0.4,
            color: '#43e97b',
            background: 'rgba(67,233,123,0.10)',
            border: '1px solid rgba(67,233,123,0.35)',
            borderRadius: 999, padding: '6px 14px',
            animation: 'orbListenHintBreathe 1.6s ease-in-out infinite',
          }}
        >
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#43e97b',
            boxShadow: '0 0 10px rgba(67,233,123,0.9)',
          }} />
          {lang === 'de' ? 'Sprich jetzt' : 'Speak now'}
        </div>
      )}

      <style>{`
        @keyframes orbListenPulse {
          0%   { transform: scale(1); opacity: 0.9; }
          70%  { transform: scale(1.18); opacity: 0; }
          100% { transform: scale(1.18); opacity: 0; }
        }
        @keyframes orbListenHintBreathe {
          0%, 100% { opacity: 0.85; transform: translateY(0); }
          50%      { opacity: 1;    transform: translateY(-1px); }
        }
      `}</style>

      {/* Status text — scrollable */}
      <div data-testid="orb-app-status" style={{
        marginTop: 24, fontSize: 14,
        color: 'rgba(255,255,255,0.65)',
        textAlign: 'center',
        maxWidth: '85vw', width: 380,
        lineHeight: 1.6,
        minHeight: 48, maxHeight: '30vh',
        overflowY: 'auto', padding: '8px 20px',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255,255,255,0.15) transparent',
      }}>
        {statusText}
      </div>

      {/* Live Match Card — appears below the Orb when backend emits orb:match */}
      {matchResult && (
        <>
          <MatchCard
            match={matchResult}
            onAccept={() => {
              // Emit accept via socket + close the card
              if (matchResult.id && window.__orbSocket) {
                window.__orbSocket.emit('orb:match:accept', { match_id: matchResult.id });
              }
              clearMatch();
            }}
            onReject={() => {
              if (matchResult.id && window.__orbSocket) {
                window.__orbSocket.emit('orb:match:reject', { match_id: matchResult.id });
              }
              clearMatch();
            }}
            onViewProfile={() => {
              if (matchResult.user_id || matchResult.id) {
                window.open(`/profile/${matchResult.user_id || matchResult.id}`, '_blank');
              }
            }}
          />
          {/* Phase B+ — Live Detour-Slider directly below the match profile.
              Visible only when the match payload carries pickup/dropoff coords
              (so the slider can run /search/corridor). */}
          <OrbDetourSlider
            visible={
              matchResult.from_lat != null && matchResult.to_lat != null
            }
            coords={{
              from: { lat: matchResult.from_lat, lng: matchResult.from_lng },
              to:   { lat: matchResult.to_lat,   lng: matchResult.to_lng   },
            }}
            initialKm={0}
            onResults={(rides) => {
              // Hot-swap the Orb's status text with live count, no console spam
              if (rides && rides.length === 0) {
                setStatusText('🔍 Keine Treffer mit diesem Umweg-Limit. Schiebe den Slider weiter rechts.');
              }
            }}
          />
        </>
      )}

      {/* Bottom hint */}
      <div style={{
        position: 'absolute', bottom: 32,
        fontSize: 12, color: 'rgba(255,255,255,0.15)',
        letterSpacing: '0.06em',
      }}>
        {isRecording ? 'Hoere zu...' : 'Continuous Listening aktiv'}
      </div>
    </div>
  );
}
